import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle, ShoppingBag, Truck, RotateCcw, CreditCard, Store as StoreIcon,
  Radio, Shield, Sparkles, Mail, Search, ChevronDown, MessageCircle, Users,
  Send, CheckCircle2, AlertCircle
} from "lucide-react";
import { api } from "../lib/api";
import VoiceMicButton from "../components/VoiceMicButton";

const SUPPORT_EMAIL = "shoprizzlive@gmail.com";

// FAQ data — categorised so the page stays scannable.
const SECTIONS = [
  {
    id: "shopping",
    label: "Shopping & orders",
    icon: ShoppingBag,
    color: "#D4FF00",
    items: [
      {
        q: "How do I place an order?",
        a: "Browse the home feed, search, or watch a live stream. Tap a product, pick the size/variant, hit Add to cart, then go to Checkout. You can pay via card, UPI, wallet balance, PayPal, Apple Pay, Google Pay, COD or Crypto (USDC).",
      },
      {
        q: "Do I have to sign up to shop?",
        a: "Guests can browse and add items to a cart, but you'll be asked to log in or create an account at checkout so we can track your order, save your wallet balance and apply rewards.",
      },
      {
        q: "Can I shop for someone else (mom, kid, partner)?",
        a: "Yes. Open Profile → Family, add a member with their relation, gender and age. Then go to Profile → Preferences and set their sizes/styles. Use the 'Shopping for…' pill at the top of your profile to switch the active shopper. Their picks auto-fill your search filters and the 'For {name}' strip on the home page.",
      },
      {
        q: "How do coupons work?",
        a: "Browse public coupons on the home screen or paste a code at checkout. The platform validates the code (active window, min order, applicable products / categories, usage limit) and shows the discount before you pay. Sellers and admins can create coupons in their dashboards.",
      },
      {
        q: "What's the wallet balance?",
        a: "Your ShopRizz wallet is a built-in store credit. New buyers get a $50 welcome bonus. You earn 5% rewards on every paid order, and refunds for cancelled / returned orders land here automatically. Use it at checkout via the 'Use wallet balance' option.",
      },
    ],
  },
  {
    id: "delivery",
    label: "Delivery & tracking",
    icon: Truck,
    color: "#00F0FF",
    items: [
      {
        q: "How long does delivery take?",
        a: "Each product shows an estimated delivery window (typically 2–10 days depending on the seller and pincode). You'll see a live timeline on the order page: Confirmed → Processing → Shipped → Out for delivery → Delivered.",
      },
      {
        q: "Do you ship internationally?",
        a: "ShopRizz is a global marketplace. Each seller specifies the pincodes / regions they ship to — if your address isn't covered for a product, the storefront will flag it before checkout.",
      },
      {
        q: "How do I track my order?",
        a: "Go to Profile → My orders or open any order detail. The tracking timeline updates as the seller moves it through fulfillment. You'll also receive in-app notifications on every status change.",
      },
    ],
  },
  {
    id: "returns",
    label: "Returns, refunds & exchanges",
    icon: RotateCcw,
    color: "#FF007F",
    items: [
      {
        q: "How do I return an item?",
        a: "After delivery, open the order detail and tap 'Request return' on any item. Pick a reason and submit. The seller will approve, schedule a pickup, and once the item is received you'll get a refund credited to your wallet.",
      },
      {
        q: "Can I cancel an order?",
        a: "Yes — as long as it hasn't been delivered, cancelled or refunded. We refund the full amount instantly to your wallet.",
      },
      {
        q: "How do exchanges work?",
        a: "Exchanges are within the same store. Open the order, hit Exchange on the item, then pick a new size or a different product from that seller. We auto-credit / debit any price difference on your wallet.",
      },
      {
        q: "When will I see my refund?",
        a: "Refunds land in your wallet immediately the moment a return is marked Received or an order is cancelled. Wallet credit can be used on the next order or — coming soon — withdrawn.",
      },
    ],
  },
  {
    id: "live",
    label: "Live shopping & videos",
    icon: Radio,
    color: "#FF007F",
    items: [
      {
        q: "What's a live stream on ShopRizz?",
        a: "Sellers go live in real time to demo, drop and sell. You can chat, react, and tap pinned products to add them to your cart while watching — like a TikTok / live mall mash-up.",
      },
      {
        q: "How do I watch?",
        a: "Tap the LIVE pill on the home screen, the bottom-nav Live tab, or any 'Live now' card. Streams have built-in chat and reactions; following the seller pushes you a notification when they go live again.",
      },
      {
        q: "What about the swipeable video feed?",
        a: "Open Videos on the bottom nav for a TikTok-style feed of seller drops and demos. Like, save and tap the product chips to buy.",
      },
    ],
  },
  {
    id: "selling",
    label: "Selling on ShopRizz",
    icon: StoreIcon,
    color: "#D4FF00",
    items: [
      {
        q: "How do I become a seller?",
        a: "Sign up and pick the Seller role (or upgrade an existing buyer account by contacting us). You get a Seller dashboard for stores, products, campaigns, coupons, analytics, orders + returns, and a Creator Studio to record videos / go live.",
      },
      {
        q: "What does it cost to sell?",
        a: "Listing is free. ShopRizz takes a 15% platform fee on completed orders; the rest is your pending payout, visible in the Analytics tab.",
      },
      {
        q: "Can I list many products at once?",
        a: "Yes — use the 'Bulk add' tab in your Seller dashboard. Paste rows in the table editor or import a CSV using the provided template. Errors are flagged per row so nothing silent gets dropped.",
      },
      {
        q: "Can I run promotions or coupons?",
        a: "Yes. Open Seller → Coupons to create % or $-off codes with start/end dates, min order, max uses, and product / category scoping. Live status (live / scheduled / expired / inactive / exhausted) is computed for you.",
      },
      {
        q: "What's the Creator Studio?",
        a: "It's where you record videos or go live. Add vibrant gradient or image backgrounds, drag-and-drop text + emoji overlays, pick a music track, and pin products to the stream so buyers can shop while watching.",
      },
    ],
  },
  {
    id: "payments",
    label: "Payments & security",
    icon: CreditCard,
    color: "#00F0FF",
    items: [
      {
        q: "Which payment methods are supported?",
        a: "Card, UPI, Wallet balance, PayPal, Apple Pay, Google Pay, Cash on Delivery and Crypto (USDC). The actual processor used depends on your country.",
      },
      {
        q: "Is my payment data safe?",
        a: "ShopRizz never stores raw card numbers. Card / UPI / wallet flows are routed to certified payment processors. JWT auth is httpOnly + Secure; passwords are hashed with bcrypt.",
      },
      {
        q: "How do I reset my password?",
        a: "Hit 'Forgot password?' on the login screen, enter your email, then use the reset token to set a new password. Tokens expire in 30 minutes.",
      },
    ],
  },
  {
    id: "account",
    label: "Account, family & preferences",
    icon: Users,
    color: "#9B5DE5",
    items: [
      {
        q: "How do preferences power my home feed?",
        a: "On Profile → Preferences, pick sizes, fits, colours, brands and more for each category. The 'For You' / 'For {member}' strip on the home page recommends products that match the active shopper's enabled preferences.",
      },
      {
        q: "Can I temporarily turn off a preference set?",
        a: "Yes — every category row in the Preferences table has an Active toggle. Off keeps your picks but skips that category from auto-applied filters and recommendations. Flip it back on anytime.",
      },
      {
        q: "How do I delete a family member or my account?",
        a: "Family members: Profile → Family → trash icon (also wipes their preferences). Account deletion: email us at shoprizzlive@gmail.com and we'll process it within 30 days.",
      },
    ],
  },
  {
    id: "policy",
    label: "Trust, safety & policy",
    icon: Shield,
    color: "#FAFAFA",
    items: [
      {
        q: "Are sellers verified?",
        a: "Sellers are vetted before going live. Admins can lock specific store fields (logo, banner, name, etc.) and moderate streams in real time. Counterfeit or unsafe listings are taken down.",
      },
      {
        q: "How do I report a problem with an order or seller?",
        a: "Open the order or store and use Report (or email us). For chat / live moderation, tap the flag icon on any message. Admins review reports daily.",
      },
      {
        q: "Where can I read your full policies?",
        a: "Detailed Terms of Service, Privacy Policy and Seller Agreement are coming to a Legal page soon. For specifics in the meantime, please email us.",
      },
    ],
  },
];

// Helpers
const flatten = (sections) =>
  sections.flatMap((s) => s.items.map((it) => ({ ...it, section: s.id, label: s.label })));

export default function FAQPage() {
  const [open, setOpen] = useState(() => new Set());
  const [query, setQuery] = useState("");

  const toggle = (key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return flatten(SECTIONS).filter(
      (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="pb-24 md:pb-12">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#3D2845]">
        <div className="absolute inset-0 grain pointer-events-none"/>
        <div className="absolute -top-32 -left-24 w-[500px] h-[500px] rounded-full bg-[#FF007F]/15 blur-[140px] pointer-events-none"/>
        <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] rounded-full bg-[#D4FF00]/15 blur-[140px] pointer-events-none"/>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00] mb-3">
            <HelpCircle size={14}/> Help center
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl uppercase leading-none mb-4">
            Got a <span className="title-gradient">question</span>?
          </h1>
          <p className="text-[#A1A1AA] text-base max-w-2xl mb-6">
            Everything you need to know about shopping, selling, going live, and getting your stuff (or your money) back.
          </p>

          <div className="relative max-w-xl flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
              <input
                data-testid="faq-search-input"
                type="text"
                placeholder="Search the FAQ — try 'refund', 'live', 'wallet'…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="input-dark w-full pl-10 pr-4 py-3 text-sm"
              />
            </div>
            <VoiceMicButton
              testid="faq-search-voice"
              onTranscript={(t) => setQuery(t)}
              size={16} className="w-11 h-11"/>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 space-y-12">
        {/* Search results override sections */}
        {matches !== null ? (
          <section data-testid="faq-search-results">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-2xl uppercase">
                Results <span className="text-[#A1A1AA] text-base font-normal">· {matches.length} match{matches.length === 1 ? "" : "es"}</span>
              </h2>
              <button onClick={() => setQuery("")} className="text-xs text-[#FF007F] hover:text-[#D4FF00]">
                Clear search
              </button>
            </div>
            {matches.length === 0 ? (
              <div className="card p-10 text-center">
                <MessageCircle size={36} className="mx-auto text-[#52525B] mb-3"/>
                <div className="font-display font-bold text-xl mb-2">No matches for "{query}"</div>
                <div className="text-sm text-[#A1A1AA] mb-4">
                  Drop us a line and we'll help you out.
                </div>
                <a href={`mailto:${SUPPORT_EMAIL}?subject=FAQ%20miss%3A%20${encodeURIComponent(query)}`}
                  className="btn-primary text-sm" data-testid="faq-email-cta-empty">
                  <Mail size={14} className="inline mr-1"/> Email support
                </a>
              </div>
            ) : (
              <div className="space-y-2">
                {matches.map((it, i) => {
                  const key = `s-${it.section}-${i}`;
                  const isOpen = open.has(key);
                  return (
                    <FaqItem key={key} item={it} keyId={key} isOpen={isOpen}
                      onToggle={toggle} sectionLabel={it.label}/>
                  );
                })}
              </div>
            )}
          </section>
        ) : (
          SECTIONS.map((sec) => (
            <Section key={sec.id} sec={sec} open={open} onToggle={toggle}/>
          ))
        )}

        {/* Contact card */}
        <section data-testid="faq-contact-card">
          <ContactForm/>
        </section>

        <div className="text-center text-xs text-[#52525B] pt-4">
          Last updated {new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" })} ·
          <Link to="/" className="hover:text-[#D4FF00] ml-1">Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", topic: "other" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 5) {
      setError("Please fill in your name, email, subject, and a message of at least 5 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/support/contact", form);
      setResult(data);
      setForm({ name: "", email: "", subject: "", message: "", topic: form.topic });
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sorry, something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="card p-8 text-center" data-testid="contact-form-success">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4FF00] text-black flex items-center justify-center mb-4">
          <CheckCircle2 size={28} strokeWidth={2.5}/>
        </div>
        <div className="font-display font-black text-2xl uppercase mb-2">Message sent</div>
        <div className="text-sm text-[#A1A1AA] max-w-md mx-auto mb-2">{result.message}</div>
        <div className="text-[11px] text-[#52525B] font-mono">Ticket #{(result.ticket_id || "").slice(-8).toUpperCase()}</div>
        <button onClick={() => setResult(null)} className="btn-secondary text-xs mt-5"
          data-testid="contact-send-another-btn">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card p-6 sm:p-8 relative overflow-hidden"
      data-testid="contact-form">
      <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-[#D4FF00]/10 blur-3xl pointer-events-none"/>
      <div className="relative">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-2xl bg-[#D4FF00] text-black flex items-center justify-center shrink-0">
            <Mail size={18} strokeWidth={2.5}/>
          </div>
          <div>
            <div className="font-display font-black text-2xl uppercase">Still curious?</div>
            <div className="text-xs text-[#A1A1AA]">We usually reply within one business day.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Your name</label>
            <input value={form.name} onChange={(e) => setField("name", e.target.value)}
              placeholder="Jane Doe" required maxLength={80}
              data-testid="contact-name-input"
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
              placeholder="you@example.com" required maxLength={120}
              data-testid="contact-email-input"
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Topic</label>
            <select value={form.topic} onChange={(e) => setField("topic", e.target.value)}
              data-testid="contact-topic-select"
              className="input-dark w-full px-3 py-2 text-sm">
              <option value="order">Order issue</option>
              <option value="seller">Seller question</option>
              <option value="bug">Bug / glitch</option>
              <option value="feedback">Feedback / idea</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Subject</label>
            <input value={form.subject} onChange={(e) => setField("subject", e.target.value)}
              placeholder="Briefly, what's up?" required maxLength={140}
              data-testid="contact-subject-input"
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Message</label>
          <textarea value={form.message} onChange={(e) => setField("message", e.target.value)}
            rows={5} maxLength={4000} required
            placeholder="Tell us what's going on. The more detail, the faster we can help."
            data-testid="contact-message-input"
            className="input-dark w-full px-3 py-2 text-sm resize-y"/>
          <div className="text-[10px] text-[#52525B] mt-1 text-right">{form.message.length} / 4000</div>
        </div>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/40 text-[#FF007F] text-xs flex items-start gap-2"
            data-testid="contact-form-error">
            <AlertCircle size={14} className="shrink-0 mt-0.5"/> {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="text-[11px] text-[#52525B]">
            Or email us directly at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#D4FF00] hover:underline">{SUPPORT_EMAIL}</a>
          </div>
          <button type="submit" disabled={busy}
            data-testid="contact-submit-btn"
            className="btn-primary inline-flex items-center justify-center gap-2 shrink-0">
            <Send size={14}/> {busy ? "Sending…" : "Send message"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Section({ sec, open, onToggle }) {
  const Icon = sec.icon;
  return (
    <section data-testid={`faq-section-${sec.id}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${sec.color}22`, color: sec.color }}>
          <Icon size={18} strokeWidth={2.5}/>
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl uppercase">{sec.label}</h2>
      </div>
      <div className="space-y-2">
        {sec.items.map((it, i) => {
          const key = `${sec.id}-${i}`;
          return (
            <FaqItem key={key} item={it} keyId={key}
              isOpen={open.has(key)} onToggle={onToggle}/>
          );
        })}
      </div>
    </section>
  );
}

function FaqItem({ item, keyId, isOpen, onToggle, sectionLabel }) {  return (
    <div className={`card overflow-hidden transition ${isOpen ? "border-[#D4FF00]" : "hover:border-[#3D2845]"}`}
      data-testid={`faq-item-${keyId}`}>
      <button
        onClick={() => onToggle(keyId)}
        data-testid={`faq-toggle-${keyId}`}
        aria-expanded={isOpen}
        className="w-full text-left px-5 py-4 flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          {sectionLabel && (
            <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-bold mb-0.5">
              {sectionLabel}
            </div>
          )}
          <div className="font-bold text-sm sm:text-base">{item.q}</div>
        </div>
        <ChevronDown
          size={18}
          className={`shrink-0 text-[#A1A1AA] transition-transform ${isOpen ? "rotate-180 text-[#D4FF00]" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-0 text-sm text-[#A1A1AA] leading-relaxed border-t border-[#3D2845]"
          data-testid={`faq-answer-${keyId}`}>
          <div className="pt-4 whitespace-pre-line">{item.a}</div>
        </div>
      )}
    </div>
  );
}
